import React from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import TagManager from 'react-gtm-module'
import { Card, Button } from 'react-bootstrap'
import {
  i18nTranslate,
  convertToBoolean,
  getGpsRoutedCreditCardOption,
  getGpsCreditCardPaymentIdentity,
  getNamefromFirstname,
  trackGTMEvents,
  application,
  getTotalPagesForPagination,
  getLiveEventStatus,
} from 'src/utils'
import { isCrossMarketAllowed } from 'src/utils/marketUtils'
import { isExcludedSavedPaymentType } from 'src/utils/paymentNameUtils'
import isEmpty from 'lodash/isEmpty'
import {
  toastState,
  AddressValidation,
  MetaDecorator,
  tokenExIframe,
  PaymentMarketForm,
  Pagination,
} from 'src/views/components'
import { PaymentCard } from './PaymentCard'
import {
  customerContainer,
  addressContainer,
  accountsAddressContainer,
  tokenExContainer,
  paymetricContainer,
  storeContainer,
  checkoutContainer,
} from 'src/models'
import {
  getActivePaymentFormData,
  getNormalizedWalletProfileProperties,
  getRestrictedCardType,
  normalizePaymetricProvider,
} from 'src/utils/paymentContainerHelper'
import './styles.scss'
import { getLocalStorage, APPConfig } from 'config/appConfig'

@observer
class PaymentBlock extends React.Component {
  @observable isCardOpen = false
  @observable show = false
  @observable addressInvalidFlag = false
  @observable recommendations = []
  @observable params = {}
  @observable formDataEnter = {}
  totalPagesLength = 0
  currentPageDetails = {}

  /**
   * @description Returns the appropriate payment container based on Paymetric flag
   * @returns {Object} tokenExContainer or paymetricContainer
   */
  getPaymentContainer = () => {
    const enableDirectPaymetricAndAccertifyIntegration = convertToBoolean(
      APPConfig.getAppConfig().enableDirectPaymetricAndAccertifyIntegration
    )
    return enableDirectPaymetricAndAccertifyIntegration
      ? paymetricContainer
      : tokenExContainer
  }

  getWalletPaymentProperties = () => {
    return getNormalizedWalletProfileProperties({
      paymentContainer: this.getPaymentContainer(),
      paymetricContainer,
      gpsPaymentOption: this.getConfiguredGPSPaymentOption(),
    })
  }

  constructor(props) {
    super(props)
  }

  async componentDidMount() {
    this.getPayments()
  }

  getPayments = async () => {
    await Promise.all([
      customerContainer.getPayment(),
      addressContainer.getProfileAddress(),
    ])
    addressContainer.addressData.length === 0 &&
      addressContainer.getProfileAddress()

    trackGTMEvents({
      event: 'nuskin-profile',
      eventType: 'Profile-Payment',
    })
  }

  getWalletPaymentId = response => {
    return (
      response?.id ||
      response?.paymentId ||
      response?.response?.id ||
      customerContainer?.lastPaymentResponse?.id ||
      ''
    )
  }

  triggerWallet3DSAuthentication = async paymentId => {
    if (!this.props?.isGPS3DSEnabledInWallet) {
      return
    }

    if (typeof this.props?.onTriggerWallet3DS !== 'function') {
      return
    }

    try {
      const response = await this.props.onTriggerWallet3DS(paymentId)
      if (response?.redirectUrl) {
        return
      }
    } catch (error) {
      toastState.setToastMessage(
        i18nTranslate(
          'payment.gpsErrorMessage',
          'We ran into a technical error, please try again after sometime'
        )
      )
    }
  }

  renderList = (item, index) => {
    return (
      !isExcludedSavedPaymentType(item.type) && (
        <PaymentCard
          cardData={item}
          key={index}
          index={index}
          pageDetails={this.currentPageDetails}
        />
      )
    )
  }

  toggleCardState = () => {
    tokenExIframe.isValidCard = true
    tokenExIframe.isCvvValidCard = true
    this.isCardOpen = !this.isCardOpen
    if (this.isCardOpen) {
      checkoutContainer.addPaymentToProfile = false
    }
  }

  findDefaultAddress = () => {
    const accountaddress =
      accountsAddressContainer.addressData?.length > 0
        ? accountsAddressContainer.addressData
        : []
    const addressData =
      addressContainer.addressData?.length > 0
        ? addressContainer.addressData
        : accountaddress
    let result = addressData.find(address => {
      if (address.isDefault === true) {
        return true
      }
    })
    return result ? result : {}
  }

  getGPSCreditCardOptions = () => {
    return checkoutContainer?.globalPaymentResponse?.CREDITCARD || []
  }

  getConfiguredGPSPaymentOption = () => {
    const gpsCreditCardOptions = this.getGPSCreditCardOptions()
    const configuredProcessorName =
      APPConfig.getAppConfig()?.gpsSavedCardProcessorName

    // Same fallback as checkout, so a GPS market without a configured processor
    // name still saves the card as GPS instead of a plain CREDITCARD
    return getGpsRoutedCreditCardOption(
      gpsCreditCardOptions,
      configuredProcessorName
    )
  }

  onAddingPaymentCard = async cardDetail => {
    const {
      cardFirstName,
      cardNumber,
      expiryDate,
      setToDefaultPayment,
      billingSameAsShipping,
      isToMakeValidationApiCall = false,
      ...addressParams
    } = cardDetail

    const addressData =
      addressContainer.addressData?.length > 0
        ? addressContainer.addressData
        : accountsAddressContainer.addressData
    if (billingSameAsShipping === true && isEmpty(addressData)) {
      toastState.setToastMessage(
        i18nTranslate(
          'address.defaultEmpty',
          'You do not have a default shipping address. '
        ),
        false
      )
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0)
      }
      return
    }
    let billingAddress =
      billingSameAsShipping === true ? this.findDefaultAddress() : addressParams
    billingAddress.email =
      addressParams.email || customerContainer?.profileResponse?.email

    if (billingAddress.lastName === null) {
      billingAddress.firstName = customerContainer.profileResponse.firstName
      billingAddress.lastName = customerContainer.profileResponse.lastName
    }
    const activeFormData = getActivePaymentFormData({
      tokenExContainer,
      paymetricContainer,
    })
    const cardProviderRaw = paymetricContainer.getCardProviderFromTokenData(
      activeFormData || {}
    )
    const cardProvider = normalizePaymetricProvider(cardProviderRaw)
    const restrictedCardType = getRestrictedCardType(cardProvider)
    if (restrictedCardType) {
      toastState.setToastMessage(
        i18nTranslate(restrictedCardType.key, restrictedCardType.fallback),
        false
      )
      return
    }

    const { type: paymentTypeForRequest, name: paymentNameForGpsType } =
      getGpsCreditCardPaymentIdentity(
        this.getGPSCreditCardOptions(),
        APPConfig.getAppConfig()?.gpsSavedCardProcessorName
      )
    const name = getNamefromFirstname(cardFirstName)

    const params = {
      type: paymentTypeForRequest,
      ...(paymentNameForGpsType && {
        name: paymentNameForGpsType,
      }),
      isDefault: setToDefaultPayment.toString(),
      // name: cardFirstName,
      // NUSKIN-893 - Unable to save payment if full name is provided
      billingAddress,
      accountInfo: {
        name: name.firstName,
        lastName: name.lastName,
        cardNumber,
        expiryMonth: expiryDate?.split('/')[0],
        expiryYear: '20' + expiryDate?.split('/')[1],
        isDefault: setToDefaultPayment.toString(),
        provider: cardProvider === 'AMEX' ? 'AMERICANEXPRESS' : cardProvider,
      },
      option: {
        addPaymentToProfile: true,
      },
      properties: {
        ...this.getWalletPaymentProperties(),
      },
    }
    delete cardDetail.expiryDate
    delete cardDetail.undefined
    if (!cardDetail.billingSameAsShipping) {
      delete cardDetail.cardFirstName
      delete cardDetail.cardLastName
      delete cardDetail.cardNumber
      delete cardDetail.expiryMonth
      delete cardDetail.expiryYear
      delete cardDetail.cardCVV
      delete cardDetail.billingSameAsShipping
      delete cardDetail.addPaymentToProfile
      delete cardDetail.paymentMethodType
      const cardValue = {
        ...cardDetail,
        // country: storeContainer.storeIDValue,
      }

      if (
        checkoutContainer.selectedMarket === 'US' ||
        checkoutContainer.selectedMarket === 'CA' ||
        isToMakeValidationApiCall
      ) {
        const res = await addressContainer.validateAddress(cardValue)

        if (res?.code === 'ADCOVAL0003') {
          // this.show = true
          this.addressInvalidFlag = true
          this.recommendations = [{ ...cardValue }, []]
          this.formDataEnter = { ...cardValue }
          this.params = { ...params }
          this.show = true
        } else if (res?.code == 'ONESOURCE_ADPLGIN_VAL_ERROR') {
          this.setState({
            show: false,
          })
          if (typeof window !== 'undefined') {
            window.scrollTo(0, 0)
          }
          toastState.setToastMessage(
            i18nTranslate('address.NotAdded', 'Address not added')
          )
          return
        } else if (res?.code == 'AOACHLD0001') {
          this.setState({
            show: false,
          })
          if (typeof window !== 'undefined') {
            window.scrollTo(0, 0)
          }
          toastState.setToastMessage(
            i18nTranslate(
              'myaccount.userHoldStatus',
              'There is an issue with your account. Please contact you local account manager.'
            )
          )
          return
        } else {
          this.addressInvalidFlag = false
          const recommendationAddress =
            res?.address?.validationDetails?.[0]?.recommendations || []
          let firstAddress = recommendationAddress?.[0] || {}
          // this.show = true
          if (Object.keys(firstAddress)?.length > 0) {
            this.recommendations = [{ ...cardValue }, ...recommendationAddress]
            this.params = { ...params }
            this.formDataEnter = { ...cardValue }
            this.show = true
          } else {
            this.formDataEnter = { ...cardValue }
            this.params = { ...params }
            this.handleClickOnPro(cardDetail)
          }
        }
      } else {
        this.addressInvalidFlag = true
        this.recommendations = [{ ...cardValue }, []]
        this.formDataEnter = { ...cardValue }
        this.params = { ...params }
        this.handleClickOnPro(cardDetail)
      }
    } else {
      const addPaymentResponse = await customerContainer.addPayment(params)
      const walletPaymentId = this.getWalletPaymentId(addPaymentResponse)
      if (
        customerContainer.isSuccessResponse(addPaymentResponse) &&
        walletPaymentId
      ) {
        await this.triggerWallet3DSAuthentication(walletPaymentId)
      }
      if (customerContainer.isPaymentCardState === false) {
        this.toggleCardState()
      }
    }
  }

  async handleClickOnPro(formData) {
    const { accountInfo } = this.params
    const reqParams = {
      // name: formData.firstName,
      // NUSKIN-893 - Unable to save payment if full name is provided
      type: this.params?.type || 'CREDITCARD',
      ...(this.params?.name && {
        name: this.params.name,
      }),
      accountInfo,
      billingAddress: {
        ...formData,
        email: this.params.billingAddress?.email,
      },
      properties: {
        ...this.getWalletPaymentProperties(),
      },
      isDefault: this.params.isDefault,
    }
    if (formData?.selectedIndex <= 0) {
      const response =
        await customerContainer.validateOriginalAddress(reqParams)
      const walletPaymentId = this.getWalletPaymentId(response)
      if (customerContainer.isSuccessResponse(response) && walletPaymentId) {
        await this.triggerWallet3DSAuthentication(walletPaymentId)
      }
    } else {
      const response = await customerContainer.addPayment(reqParams)
      const walletPaymentId = this.getWalletPaymentId(response)
      if (customerContainer.isSuccessResponse(response) && walletPaymentId) {
        await this.triggerWallet3DSAuthentication(walletPaymentId)
      }
    }
    this.toggleCardState()
    this.handleClose()
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0)
    }
  }
  handleClose = () => {
    this.show = false
  }

  handlePagination = (activePage = 1) => {
    customerContainer.getPayment(activePage)
    if (typeof window === 'object') {
      window.scrollTo(0, 0)
    }
  }

  selectPaymentMethodPropertiesForGTM = paymentMethodKeys => {
    const { id, name, isDefault, status, userId, type, valueType } =
      paymentMethodKeys
    return { id, name, isDefault, status, userId, type, valueType }
  }

  /**
   * @description
   * Description placeholder
   * @date 11/27/2023 - 12:44:43 PM
   * Pagination component props
   * totalPages( as totalCount ) to be received from get all payments api
   * active( as page) to be received from get all payments api
   * @returns {*}
   */
  render() {
    const { pageableInfo = {}, payments = [] } =
      customerContainer?.paymentResponse || {}
    const { page = 1, count = 1 } = pageableInfo || {}

    this.currentPageDetails = {
      page: count == 1 ? page - 1 : page,
    }
    this.totalPagesLength = getTotalPagesForPagination(pageableInfo)
    const isPaymentPaginationEnabled =
      APPConfig?.getAppConfig()?.isPaymentPaginationEnabled === 'true' || false

    const paymentCardView = payments?.length
      ? payments?.map(this.renderList)
      : ''

    const myAccountPaymentTitle = i18nTranslate(
      'myAccountPaymentPage.title',
      'Payments | Nu Skin'
    )
    if (payments?.length > 0) {
      const paymentMethodDetailsForGTM = payments?.map(
        this.selectPaymentMethodPropertiesForGTM
      )
      const enableLiveEvent = getLiveEventStatus()
      TagManager.dataLayer({
        dataLayer: {
          event: 'NuskinProfilePaymentMethods',
          pagePath: '/myaccount/payments',
          pageTitle: 'Payments',
          paymentMethods: paymentMethodDetailsForGTM,
          platform: 'equinox',
        },
      })

      if (enableLiveEvent === 'true') {
        TagManager.dataLayer({
          dataLayer: {
            event: 'view_payment_methods',
          },
        })
      }
    }

    const isCrossMarketAllowedFlag =
      customerContainer.isCrossMarketAllowed() || false

    return (
      <>
        <MetaDecorator
          title={myAccountPaymentTitle}
          imageUrl=""
          imageAlt=""
          description="The Payments Page"
          location={this.props}
        />
        <section
          className={
            isCrossMarketAllowedFlag ? `wallet-payment-block-wrapper` : 'd-none'
          }>
          <Card className={`bg-light`}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-end pb-2">
                <Card.Title>
                  <h2
                    className="payment-method-title"
                    data-testid="qa-payment-method-title">
                    {i18nTranslate(
                      'payment.paymentMethodLabel',
                      'Payment Methods'
                    )}
                  </h2>
                </Card.Title>
                {!this.props?.hideAddNewCard && (
                  <Button
                    style={{
                      pointerEvents: this.isCardOpen ? 'none' : '',
                    }}
                    variant={this.isCardOpen ? 'light' : 'primary'}
                    className="payment-btn px-4"
                    onClick={this.toggleCardState}
                    data-testid="qa-add-payment-button">
                    {i18nTranslate('payment.addNewCard', 'Add New Card')}
                  </Button>
                )}
              </div>
              {this.show && (
                <AddressValidation
                  open={this.show}
                  addressInvalidFlag={this.addressInvalidFlag}
                  recommendations={this.recommendations}
                  handleClose={() => this.handleClose()}
                  handleClickOnPro={dataAfterValidation =>
                    this.handleClickOnPro(dataAfterValidation)
                  }
                />
              )}

              {this.isCardOpen && (
                <div className="add-new-payment bg-white p-3">
                  <PaymentMarketForm
                    isFromPayment={true}
                    includePaymetricCvv={false}
                    onCancel={this.toggleCardState}
                    onSubmitForm={this.onAddingPaymentCard}
                    billingAddressType="paymentsMethod"
                  />
                </div>
              )}
              <div className={`d-lg-flex payment-cards flex-wrap `}>
                {paymentCardView}
              </div>
            </Card.Body>
          </Card>
        </section>

        {this.totalPagesLength > 1 && isPaymentPaginationEnabled && (
          <Pagination
            handlePagination={this.handlePagination}
            totalPages={this.totalPagesLength}
            active={page}
            numOfPaginationsToShow={this.totalPagesLength}
            alignPagination={application.isMobile ? 'center' : 'start'}
          />
        )}
        <div
          className={
            isCrossMarketAllowedFlag ? 'd-none' : 'non-home-market-country'
          }>
          {i18nTranslate(
            'myprofile.submit.message',
            'Please switch to your home market to make any changes.'
          )}
        </div>
      </>
    )
  }
}

export { PaymentBlock }
export default PaymentBlock
